import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Plus, Pencil, Trash2, Eye, Power, Save } from "lucide-react";
import { toast } from "sonner";
import { useSiteData } from "../../context/SiteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls = "w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand)]/30";
const cardCls = "rounded-3xl bg-white border border-slate-100 p-6 shadow-[0_16px_40px_-20px_rgba(11,19,43,0.15)]";

const DECORATIONS = [
  ["snow", "Falling snow"],
  ["lights", "Christmas lights"],
  ["trees", "Christmas trees"],
  ["pumpkins", "Halloween ghosts"],
  ["leaves", "Autumn leaves"],
  ["fireworks", "Fireworks"],
];

const TEMPLATES = ["christmas", "halloween", "thanksgiving", "newyear", "easter", "valentines", "summer", "autumn", "winter", "nationalday", "custom"];

const EMPTY = {
  name: "", occasion: "custom", enabled: false,
  eyebrow: "", headline: "", description: "", button_text: "", promo_code: "",
  image: "", image_mobile: "", announcement: "", pages: ["home"], popup: false,
  decorations: { snow: false, lights: false, trees: false, pumpkins: false, leaves: false, fireworks: false },
};

export const ThemesTab = () => {
  const { refreshTheme } = useSiteData();
  const [themes, setThemes] = useState([]);
  const [editing, setEditing] = useState(null); // theme object being edited, or null
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/themes`, { withCredentials: true });
      setThemes(data);
    } catch {
      toast.error("Could not load themes.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing.name.trim()) {
      toast.error("Theme name is required.");
      return;
    }
    setBusy(true);
    try {
      if (editing.id) {
        await axios.put(`${API}/themes/${editing.id}`, editing, { withCredentials: true });
      } else {
        await axios.post(`${API}/themes`, editing, { withCredentials: true });
      }
      setEditing(null);
      await load();
      refreshTheme();
      toast.success("Published — the change is live on the website now.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed — please retry.");
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (t) => {
    try {
      const { data } = await axios.post(`${API}/themes/${t.id}/toggle`, {}, { withCredentials: true });
      // Activating one theme disables all others — mirror that instantly in the list.
      setThemes((prev) =>
        prev.map((x) => (x.id === t.id ? { ...x, enabled: data.enabled } : data.enabled ? { ...x, enabled: false } : x))
      );
      toast.success(data.enabled ? `${t.name} is live on the website now.` : `${t.name} is off — the standard design is restored.`);
      refreshTheme();
    } catch {
      toast.error("Could not switch the theme.");
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/themes/${t.id}`, { withCredentials: true });
      setThemes((prev) => prev.filter((x) => x.id !== t.id));
      toast.success("Theme deleted.");
    } catch {
      toast.error("Could not delete the theme.");
    }
  };

  const preview = (t) => window.open(`/?theme_preview=${t.id}`, "_blank", "noopener");

  const F = (label, key, props = {}) => (
    <label className="block" key={key}>
      <span className="text-xs font-bold uppercase tracking-wide text-slate-400">{label}</span>
      <input
        value={editing[key]}
        onChange={(e) => setEditing({ ...editing, [key]: e.target.value })}
        className={`${inputCls} mt-1.5`}
        data-testid={`theme-field-${key}`}
        {...props}
      />
    </label>
  );

  return (
    <div className="space-y-6" data-testid="themes-tab">
      <div className={cardCls}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="font-display font-bold text-lg text-[#0b132b]">Seasonal Themes</h2>
            <p className="text-xs text-slate-400 mt-1">
              Full manual control — turn a theme ON and it appears within seconds; OFF restores the standard design. Optionally add dates in the Design Themes studio to auto-show a theme during a window. Only one theme is ever live.
            </p>
          </div>
          <button
            onClick={() => setEditing({ ...EMPTY })}
            data-testid="theme-create-button"
            className="inline-flex items-center gap-2 rounded-full bg-[var(--brand)] text-white text-sm font-bold px-5 py-2.5 shadow-lg shadow-[var(--brand)]/30 hover:bg-[var(--brand-dark)] transition-colors"
          >
            <Plus className="w-4 h-4" /> Create Custom Theme
          </button>
        </div>
      </div>

      <div className="grid gap-4" data-testid="themes-list">
        {themes.map((t) => (
          <div key={t.id} className={`${cardCls} !p-5`} data-testid={`theme-card-${t.occasion}`}>
            <div className="flex flex-wrap items-center gap-4">
              <button
                onClick={() => toggle(t)}
                data-testid={`theme-toggle-${t.occasion}`}
                aria-label={t.enabled ? "Deactivate" : "Activate"}
                className={`w-12 h-7 rounded-full transition-colors relative shrink-0 ${t.enabled ? "bg-[var(--brand)]" : "bg-slate-200"}`}
              >
                <span className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-all ${t.enabled ? "start-6" : "start-1"}`} />
              </button>
              <div className="min-w-0 flex-1">
                <p className="font-bold text-[#0b132b] flex items-center gap-2 flex-wrap">
                  {t.name}
                  {t.enabled && (
                    <span className="text-[10px] font-bold uppercase tracking-wide bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5">Live now</span>
                  )}
                  {t.has_draft && (
                    <span className="text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-700 rounded-full px-2 py-0.5">Unpublished draft</span>
                  )}
                </p>
                <p className="text-xs text-slate-400 mt-0.5">Manual control — on/off takes effect immediately</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => preview(t)} data-testid={`theme-preview-${t.occasion}`} className="p-2.5 rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50 transition-colors" aria-label="Preview">
                  <Eye className="w-4 h-4" />
                </button>
                <button onClick={() => setEditing({ ...EMPTY, ...t })} data-testid={`theme-edit-${t.occasion}`} className="p-2.5 rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50 transition-colors" aria-label="Edit">
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => remove(t)} data-testid={`theme-delete-${t.occasion}`} className="p-2.5 rounded-xl border border-red-100 text-red-400 hover:bg-red-50 transition-colors" aria-label="Delete">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className={cardCls} data-testid="theme-editor">
          <h3 className="font-display font-bold text-lg text-[#0b132b] mb-5">
            {editing.id ? `Edit: ${editing.name}` : "Create Custom Theme"}
          </h3>
          <div className="grid sm:grid-cols-2 gap-4">
            {F("Theme name", "name", { placeholder: "e.g. Christmas" })}
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-wide text-slate-400">Template</span>
              <select
                value={editing.occasion}
                onChange={(e) => setEditing({ ...editing, occasion: e.target.value })}
                className={`${inputCls} mt-1.5`}
                data-testid="theme-field-occasion"
              >
                {TEMPLATES.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </label>
            {F("Eyebrow (small red label)", "eyebrow", { placeholder: "Christmas — Limited Season" })}
            {F("Headline", "headline", { placeholder: "Magical Christmas in Switzerland." })}
            <label className="block sm:col-span-2">
              <span className="text-xs font-bold uppercase tracking-wide text-slate-400">Description</span>
              <textarea
                value={editing.description}
                onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                rows={3}
                className={`${inputCls} mt-1.5`}
                data-testid="theme-field-description"
              />
            </label>
            {F("Button text", "button_text", { placeholder: "Book Your Christmas Tour" })}
            {F("Promo code (optional)", "promo_code", { placeholder: "XMAS10" })}
            {F("Banner image URL (desktop)", "image", { placeholder: "/api/media/file/… or https://…" })}
            {F("Banner image URL (mobile, optional)", "image_mobile")}
            <label className="block sm:col-span-2">
              <span className="text-xs font-bold uppercase tracking-wide text-slate-400">Announcement bar text (optional — slim strip at the top of the homepage)</span>
              <input
                value={editing.announcement}
                onChange={(e) => setEditing({ ...editing, announcement: e.target.value })}
                className={`${inputCls} mt-1.5`}
                data-testid="theme-field-announcement"
                placeholder="Leave empty for no announcement bar"
              />
            </label>
            <div className="block">
              <span className="text-xs font-bold uppercase tracking-wide text-slate-400">Show on pages</span>
              <div className="mt-2 flex gap-4">
                {[["home", "Homepage"], ["landing", "Tour landing pages"]].map(([val, label]) => (
                  <label key={val} className="flex items-center gap-2 text-sm font-semibold text-slate-600">
                    <input
                      type="checkbox"
                      checked={editing.pages.includes(val)}
                      onChange={(e) =>
                        setEditing({ ...editing, pages: e.target.checked ? [...editing.pages, val] : editing.pages.filter((p) => p !== val) })
                      }
                      data-testid={`theme-page-${val}`}
                      className="w-4 h-4 accent-[var(--brand)]"
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">Decorations</p>
              <div className="grid grid-cols-2 gap-2">
                {DECORATIONS.map(([key, label]) => (
                  <label key={key} className="flex items-center gap-2 text-sm text-slate-600">
                    <input
                      type="checkbox"
                      checked={!!editing.decorations[key]}
                      onChange={(e) => setEditing({ ...editing, decorations: { ...editing.decorations, [key]: e.target.checked } })}
                      data-testid={`theme-deco-${key}`}
                      className="w-4 h-4 accent-[var(--brand)]"
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">Extras</p>
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={editing.popup}
                  onChange={(e) => setEditing({ ...editing, popup: e.target.checked })}
                  data-testid="theme-popup-toggle"
                  className="w-4 h-4 accent-[var(--brand)]"
                />
                Show promotional popup (once per visitor session)
              </label>
              <label className="mt-2 flex items-center gap-2 text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={editing.enabled}
                  onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })}
                  data-testid="theme-enabled-toggle"
                  className="w-4 h-4 accent-[var(--brand)]"
                />
                Enabled (goes live on the website immediately)
              </label>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              onClick={save}
              disabled={busy}
              data-testid="theme-save-button"
              className="inline-flex items-center gap-2 rounded-full bg-[var(--brand)] text-white text-sm font-bold px-6 py-2.5 shadow-lg shadow-[var(--brand)]/30 hover:bg-[var(--brand-dark)] transition-colors disabled:opacity-60"
            >
              <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save & Publish"}
            </button>
            <button
              onClick={() => setEditing(null)}
              data-testid="theme-cancel-button"
              className="rounded-full border border-slate-200 text-slate-500 text-sm font-bold px-6 py-2.5 hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
